import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  Alert,
  Divider,
} from '@mui/material';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useSnackbar } from 'notistack';
import { useSessionState } from '../../context/session/session';
import {
  isPushSupported,
  subscribeToPush,
  getPushSubscription,
  getPushSupportDiagnostics,
  getPermissionState,
} from '../../utils/pushNotifications';
import { getAnonymousToken } from './SaveFilterButton';

const SET_NOTIFICATION_PREFERENCE = gql`
  mutation SetNotificationPreference($input: NotificationPreferenceInput!) {
    setNotificationPreference(input: $input) {
      id
      notifyActors
      notifyEvents
      notifyArticles
      notifyRecipes
      notifyPetitesAnnonces
      unsubscribeToken
      channelEmail
      channelPush
      frequency
      email
      emailConfirmed
      isActive
    }
  }
`;

const REMOVE_NOTIFICATION_PREFERENCE = gql`
  mutation RemoveNotificationPreference($savedFilterId: Int!) {
    removeNotificationPreference(savedFilterId: $savedFilterId)
  }
`;

const REQUEST_EMAIL_CONFIRMATION = gql`
  mutation RequestNotificationEmailConfirmation($savedFilterId: Int!, $email: String!) {
    requestNotificationEmailConfirmation(savedFilterId: $savedFilterId, email: $email)
  }
`;

const REGISTER_PUSH = gql`
  mutation RegisterPushSubscription($input: PushSubscriptionInput!, $anonymousToken: String) {
    registerPushSubscription(input: $input, anonymousToken: $anonymousToken) {
      id
      endpoint
    }
  }
`;

interface NotificationPreference {
  id?: string | number;
  notifyActors?: boolean;
  notifyEvents?: boolean;
  notifyArticles?: boolean;
  notifyRecipes?: boolean;
  notifyPetitesAnnonces?: boolean;
  unsubscribeToken?: string;
  channelEmail?: boolean;
  channelPush?: boolean;
  frequency?: string;
  email?: string;
  emailConfirmed?: boolean;
  isActive?: boolean;
}

interface FilterData {
  id: string | number;
  name: string;
  notificationPreference?: NotificationPreference;
}

interface NotificationConfigDialogProps {
  open: boolean;
  filter: FilterData;
  onClose: () => void;
}

function NotificationConfigDialog({ open, filter, onClose }: NotificationConfigDialogProps) {
  const user = useSessionState();
  const { enqueueSnackbar } = useSnackbar();

  const existingPref = filter?.notificationPreference;
  const isDeactivated = existingPref && !existingPref.isActive;

  const [notifyActors, setNotifyActors] = useState(false);
  const [notifyEvents, setNotifyEvents] = useState(false);
  const [notifyArticles, setNotifyArticles] = useState(false);
  const [notifyRecipes, setNotifyRecipes] = useState(false);
  const [notifyPetitesAnnonces, setNotifyPetitesAnnonces] = useState(false);
  const [channelEmail, setChannelEmail] = useState(false);
  const [channelPush, setChannelPush] = useState(false);
  const [frequency, setFrequency] = useState('daily');
  const [email, setEmail] = useState(user?.email || '');
  const [showReactivate, setShowReactivate] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (existingPref && existingPref.isActive) {
      setNotifyActors(existingPref.notifyActors || false);
      setNotifyEvents(existingPref.notifyEvents || false);
      setNotifyArticles(existingPref.notifyArticles || false);
      setNotifyRecipes(existingPref.notifyRecipes || false);
      setNotifyPetitesAnnonces(existingPref.notifyPetitesAnnonces || false);
      setChannelEmail(existingPref.channelEmail || false);
      setChannelPush(existingPref.channelPush || false);
      setFrequency(existingPref.frequency || 'daily');
      setEmail(existingPref.email || user?.email || '');
      setShowReactivate(false);
    } else {
      setNotifyActors(false);
      setNotifyEvents(false);
      setNotifyArticles(false);
      setNotifyRecipes(false);
      setChannelEmail(false);
      setChannelPush(false);
      setFrequency('daily');
      setEmail(user?.email || '');
      setShowReactivate(!!isDeactivated);
    }
  }, [open]);

  const [pushDiagnostics, setPushDiagnostics] = useState(() => getPushSupportDiagnostics());
  const pushSupported = pushDiagnostics.supported;
  const [pushPermissionState, setPushPermissionState] = useState('default');

  const [setNotificationPreference, { loading: savingPref }] = useMutation(SET_NOTIFICATION_PREFERENCE);
  const [removeNotificationPreference] = useMutation(REMOVE_NOTIFICATION_PREFERENCE);
  const [requestEmailConfirmation] = useMutation(REQUEST_EMAIL_CONFIRMATION);
  const [registerPush] = useMutation(REGISTER_PUSH);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPushDiagnostics(getPushSupportDiagnostics());
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!open || !pushSupported) {
      return undefined;
    }

    getPermissionState().then((state) => {
      if (!cancelled) {
        setPushPermissionState(state);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [open, pushSupported]);

  const handleSave = async () => {
    if (!notifyActors && !notifyEvents && !notifyArticles && !notifyRecipes && !notifyPetitesAnnonces) {
      enqueueSnackbar('Choisissez au moins un type de notification', { variant: 'warning' });
      return;
    }
    if (!channelEmail && !channelPush) {
      enqueueSnackbar('Choisissez au moins un canal de notification', { variant: 'warning' });
      return;
    }

    setSaving(true);
    let effectiveChannelPush = channelPush;

    try {
      if (effectiveChannelPush) {
        if (!pushSupported) {
          enqueueSnackbar('Les notifications push ne sont pas disponibles sur cet appareil. Le canal email reste activé.', { variant: 'warning' });
          effectiveChannelPush = false;
          setChannelPush(false);
          if (!channelEmail) {
            enqueueSnackbar('Choisissez au moins un canal de notification', { variant: 'warning' });
            setSaving(false);
            return;
          }
        } else {
          try {
            const pushWithTimeout = (promise: Promise<any>) =>
              Promise.race([
                promise,
                new Promise((_, reject) =>
                  setTimeout(() => reject(new Error("Délai dépassé pour l'activation push. Essayez de recharger la page.")), 10000),
                ),
              ]);

            const existingSub = await pushWithTimeout(getPushSubscription());
            if (!existingSub) {
              const sub = await pushWithTimeout(subscribeToPush());
              setPushPermissionState('granted');
              const anonymousToken = user ? null : getAnonymousToken();
              await registerPush({
                variables: {
                  input: {
                    endpoint: (sub as any).endpoint,
                    p256dh: (sub as any).p256dh,
                    auth: (sub as any).auth,
                  },
                  anonymousToken,
                },
              });
            }
          } catch (pushError: any) {
            try {
              const permissionState = await getPermissionState();
              setPushPermissionState(permissionState);
            } catch (_) { /* ignore */ }
            console.error('[Push] Activation error:', pushError);
            enqueueSnackbar(
              channelEmail
                ? 'Push impossible, la notification sera envoyée par email uniquement.'
                : (pushError.message || "Impossible d'activer les notifications push."),
              { variant: channelEmail ? 'warning' : 'error' },
            );
            effectiveChannelPush = false;
            setChannelPush(false);
            if (!channelEmail) {
              setSaving(false);
              return;
            }
          }
        }
      }

      const needsEmail = channelEmail && !user;
      if (needsEmail && !email) {
        enqueueSnackbar('Veuillez renseigner votre email', { variant: 'warning' });
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (needsEmail && !emailRegex.test(email)) {
        enqueueSnackbar('Email invalide', { variant: 'warning' });
        return;
      }

      await setNotificationPreference({
        variables: {
          input: {
            savedFilterId: parseInt(String(filter.id)),
            notifyActors,
            notifyEvents,
            notifyArticles,
            notifyRecipes,
            notifyPetitesAnnonces,
            channelEmail,
            channelPush: effectiveChannelPush,
            frequency,
            email: needsEmail ? email : (user?.email || null),
          },
        },
      });

      if (needsEmail && (!existingPref?.emailConfirmed || existingPref?.email !== email)) {
        try {
          await requestEmailConfirmation({
            variables: {
              savedFilterId: parseInt(String(filter.id)),
              email,
            },
          });
          enqueueSnackbar('Un email de confirmation vous a été envoyé', { variant: 'info' });
        } catch (e) {
          console.error(e);
        }
      }

      enqueueSnackbar('Préférences de notification sauvegardées !', { variant: 'success' });
      onClose();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Erreur lors de la sauvegarde', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      await removeNotificationPreference({
        variables: { savedFilterId: parseInt(String(filter.id)) },
      });
      enqueueSnackbar('Notifications désactivées', { variant: 'success' });
      onClose();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Erreur lors de la désactivation', { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Notifications pour "{filter.name}"
      </DialogTitle>
      <DialogContent>
        {showReactivate && (
          <Alert severity="info" style={{ marginBottom: 16 }}>
            Les notifications pour ce filtre sont désactivées.
            <br />
            Cochez les options ci-dessous puis cliquez sur <strong>Réactiver</strong> pour les activer à nouveau.
          </Alert>
        )}
        <Typography variant="subtitle2" gutterBottom style={{ marginTop: 8 }}>
          Me notifier pour :
        </Typography>
        <FormControlLabel
          control={<Checkbox checked={notifyActors} onChange={(e) => setNotifyActors(e.target.checked)} />}
          label="Nouveaux acteurs"
        />
        <FormControlLabel
          control={<Checkbox checked={notifyEvents} onChange={(e) => setNotifyEvents(e.target.checked)} />}
          label="Nouveaux événements"
        />
        <FormControlLabel
          control={<Checkbox checked={notifyArticles} onChange={(e) => setNotifyArticles(e.target.checked)} />}
          label="Nouveaux articles"
        />
        <FormControlLabel
          control={<Checkbox checked={notifyRecipes} onChange={(e) => setNotifyRecipes(e.target.checked)} />}
          label="Nouvelles recettes"
        />
        <FormControlLabel
          control={<Checkbox checked={notifyPetitesAnnonces} onChange={(e) => setNotifyPetitesAnnonces(e.target.checked)} />}
          label="Nouvelles petites annonces"
        />

        <Divider style={{ margin: '16px 0' }} />

        <Typography variant="subtitle2" gutterBottom>
          Canal de notification :
        </Typography>
        <FormControlLabel
          control={<Checkbox checked={channelEmail} onChange={(e) => setChannelEmail(e.target.checked)} />}
          label="Email"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={channelPush}
              onChange={(e) => setChannelPush(e.target.checked)}
              disabled={!pushSupported}
            />
          }
          label="Push notification"
        />
        {!pushSupported && (
          <Alert severity="error" style={{ marginTop: 8 }}>
            {pushDiagnostics.reason || 'Les notifications push ne sont pas supportées par votre navigateur'}
          </Alert>
        )}
        {pushSupported && pushPermissionState === 'default' && (
          <Alert severity="info" style={{ marginTop: 8 }}>
            En activant ce canal, votre navigateur affichera une demande d'autorisation pour les notifications.
          </Alert>
        )}
        {pushSupported && pushPermissionState === 'denied' && (
          <Alert severity="warning" style={{ marginTop: 8 }}>
            <strong>Les notifications sont bloquées pour ce site.</strong>
            <br /><br />
            <strong>📱 Sur Android (Chrome) :</strong>
            <ol style={{ margin: '4px 0', paddingLeft: 20 }}>
              <li>Appuyez sur l'icône <strong>🔒 (cadenas)</strong> ou <strong>ⓘ</strong> à gauche de la barre d'adresse</li>
              <li>Appuyez sur <strong>« Autorisations »</strong> ou <strong>« Paramètres du site »</strong></li>
              <li>Trouvez <strong>« Notifications »</strong> et sélectionnez <strong>« Autoriser »</strong></li>
              <li>Rechargez la page</li>
            </ol>
            <strong>💻 Sur ordinateur (Chrome, Edge, Brave) :</strong>
            <ol style={{ margin: '4px 0', paddingLeft: 20 }}>
              <li>Cliquez sur l'icône <strong>🔒</strong> à gauche de l'adresse</li>
              <li>Cliquez sur <strong>« Paramètres du site »</strong></li>
              <li>Mettez Notifications sur <strong>« Autoriser »</strong></li>
            </ol>
            <strong>🦊 Sur Firefox :</strong> cliquez sur le cadenas → réactivez les permissions pour ce site.
          </Alert>
        )}

        {channelEmail && !user && (
          <Box mt={2}>
            <TextField
              fullWidth
              variant="outlined"
              label="Votre adresse email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              size="small"
              inputProps={{ maxLength: 255 }}
            />
            {existingPref?.email && !existingPref?.emailConfirmed && (
              <Alert severity="warning" style={{ marginTop: 8 }}>
                Email non confirmé. Vérifiez votre boîte de réception.
              </Alert>
            )}
          </Box>
        )}

        <Divider style={{ margin: '16px 0' }} />

        <FormControl fullWidth variant="outlined" size="small">
          <InputLabel>Fréquence</InputLabel>
          <Select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as string)}
            label="Fréquence"
          >
            <MenuItem value="immediate">Immédiat</MenuItem>
            <MenuItem value="daily">Quotidien</MenuItem>
            <MenuItem value="weekly">Hebdomadaire</MenuItem>
          </Select>
        </FormControl>

        {notifyEvents && existingPref?.unsubscribeToken && existingPref?.isActive && (
          <>
            <Divider style={{ margin: '16px 0' }} />
            <Typography variant="subtitle2" gutterBottom>
              📅 Synchronisation calendrier
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Deux options pour avoir vos événements OUAAA dans votre agenda personnel :
              <br />
              <strong>1. S'abonner</strong> (à la manière d'un flux RSS) — collez l'URL ci-dessous
              dans votre logiciel d'agenda. Les événements correspondant à ce filtre s'ajouteront et
              se mettront à jour automatiquement.
              <br />
              <strong>2. Télécharger</strong> le fichier <code>.ics</code> et l'importer
              manuellement (sans mise à jour automatique).
            </Typography>
            {(() => {
              const baseUrl =
                typeof window !== 'undefined' ? window.location.origin : 'https://ouaaa-transition.fr';
              const icsUrl = `${baseUrl}/api/ics/${existingPref.unsubscribeToken}`;
              return (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField
                    value={icsUrl}
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true }}
                    onFocus={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      try {
                        navigator.clipboard?.writeText(icsUrl);
                      } catch {}
                    }}
                  >
                    Copier
                  </Button>
                  <Button size="small" variant="text" href={icsUrl} target="_blank">
                    Télécharger (.ics)
                  </Button>
                </Box>
              );
            })()}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Cette URL fonctionne avec tous les agendas qui acceptent le format iCal/ICS
              (compatible avec la majorité des applications de calendrier sur mobile et ordinateur).
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {existingPref?.isActive && (
          <Button onClick={handleDeactivate} color="error">
            Désactiver
          </Button>
        )}
        <Button onClick={onClose} disabled={saving}>Annuler</Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={saving || savingPref}>
          {saving ? 'Activation...' : (showReactivate ? 'Réactiver' : (existingPref?.isActive ? 'Sauvegarder' : 'Activer'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default NotificationConfigDialog;
